import { c as _c } from "react/compiler-runtime";
import { Avatar, Skeleton } from '@systemeio/components';
import { BankNote01Icon, BlogNewIcon, FilterFunnel01Icon, GraduationHat01Icon, ShoppingBag01Icon, User01Icon } from '@systemeio/icons';
import { centsToUnits, replaceWwwForHosts } from '@systemeio/util';
import { DetailsList } from 'modules/subaccounts/components/details-list';
import { subAccountDefaultCreateValues, subAccountDefaultErrors } from 'modules/subaccounts/constants/sub-account-default-values';
import { SubAccountsLoco } from 'modules/subaccounts/constants/sub-accounts-loco';
import { useSubAccounts } from 'modules/subaccounts/hooks/use-sub-accounts';
import { useSubAccountsActions } from 'modules/subaccounts/hooks/use-sub-accounts-actions';
import { SubAccountCreateInterface, SubAccountErrorsInterface, SubAccountInterface } from 'modules/subaccounts/types/sub-account-interface';
import { getSubAccountNewLeadsStatistics } from 'modules/subaccounts/utils/get-sub-account-new-leads-statistics';
import { findResourceByUserId } from 'modules/subaccounts/utils/get-sub-account-resources';
import { getSubAccountSalesStatistics } from 'modules/subaccounts/utils/get-sub-account-sales-statistics';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { FormInput, FormInputGroup, Hint } from 'shared/components';
import ListingLayout from 'shared/components/listing-layout';
import { RelativeTime } from 'shared/components/relative-time';
import { isClient } from 'shared/components/screen-height-div';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { generatePretendUserUrl } from 'shared/hooks/use-user/utils/generate-pretend-user-url';
import { hasSubAccounts } from 'shared/hooks/use-user/utils/has-subaccounts';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { currencyFormat } from 'shared/utils/currency-format';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
import { getBaseServerSideProps, getCombinedServerSideProps } from 'shared/utils/get-base-server-side-props';
import { generatePathFromName } from 'shared/utils/path-utils';
const iconProps = {
  width: 20,
  height: 20,
  className: 'text-icon-secondary'
};
function SubAccounts() {
  const $ = _c(119);
  const {
    t,
    tICU
  } = useLocoTranslation();
  const {
    user,
    mutate: mutateUser
  } = useUser();
  const router = useRouter();
  const {
    deleteSubAccount,
    createSubAccount,
    editSubAccount
  } = useSubAccountsActions();
  const [errors, setErrors] = useState(subAccountDefaultErrors);
  const {
    setPagination,
    pagination,
    setLimit,
    limit
  } = usePagination();
  let t0;
  if ($[0] !== limit || $[1] !== pagination) {
    t0 = {
      pagination,
      limit
    };
    $[0] = limit;
    $[1] = pagination;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    subAccounts,
    counter,
    mutate,
    resources,
    isLoadingResources,
    sales,
    isLoadingSales,
    newLeads,
    isLoadingNewLeads,
    revalidateSubAccountsModule
  } = useSubAccounts(t0);
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setErrors(subAccountDefaultErrors);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onModalClose = t1;
  const renderName = _temp;
  let t2;
  if ($[4] !== isLoadingResources || $[5] !== resources?.items || $[6] !== t) {
    t2 = subAccount_0 => {
      const data = findResourceByUserId(subAccount_0.user.id, resources?.items);
      if (isLoadingResources) {
        return <Skeleton className="rounded-xs py-2.5" />;
      }
      if (!data) {
        return null;
      }
      const resourcesList = [<><span>{t("global.created_on")}:</span><span className="truncate"><RelativeTime time={data.user.createdAt} /></span></>, <><User01Icon {...iconProps} /><span>{data.contacts}</span>{t("dashboard.sub_accounts.contacts")}</>, <><FilterFunnel01Icon {...iconProps} /><span>{data.funnels}</span>{t("dashboard.sub_accounts.funnels")}</>, <><BlogNewIcon {...iconProps} /><span>{data.blogs}</span>{t("dashboard.sub_accounts.blogs")}</>, <><GraduationHat01Icon {...iconProps} /><span>{data.courses}</span>{t("dashboard.sub_accounts.courses")}</>, <><ShoppingBag01Icon {...iconProps} /><span>{data.digitalStores}</span>{t("dashboard.sub_accounts.digital_stores")}</>];
      return <DetailsList rows={resourcesList} />;
    };
    $[4] = isLoadingResources;
    $[5] = resources?.items;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const renderResources = t2;
  let t3;
  if ($[8] !== isLoadingNewLeads || $[9] !== isLoadingSales || $[10] !== newLeads?.items || $[11] !== sales?.items || $[12] !== t || $[13] !== user?.dashboardLocale) {
    t3 = subAccount_1 => {
      if (isLoadingSales || isLoadingNewLeads) {
        return <Skeleton className="rounded-xs py-2.5" />;
      }
      const statisticsList = [<><BankNote01Icon {...iconProps} /><div className="flex flex-col gap-1">{getSubAccountSalesStatistics(subAccount_1, sales?.items)?.sales.map(t4 => {
            const {
              currency,
              value
            } = t4;
            return <span key={currency}>{currencyFormat(centsToUnits(value), currency, user?.dashboardLocale)}</span>;
          })}</div>{t("dashboard.sub_accounts.sales")}</>, <><FilterFunnel01Icon {...iconProps} /><div className="flex flex-col gap-1">{getSubAccountNewLeadsStatistics(subAccount_1, newLeads?.items)?.newLeads}</div>{t("dashboard.sub_accounts.new_leads")}</>];
      return <DetailsList rows={statisticsList} />;
    };
    $[8] = isLoadingNewLeads;
    $[9] = isLoadingSales;
    $[10] = newLeads?.items;
    $[11] = sales?.items;
    $[12] = t;
    $[13] = user?.dashboardLocale;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const renderStatistics = t3;
  let t4;
  if ($[15] !== t) {
    t4 = t("global.name");
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t4) {
    t5 = {
      key: "name",
      header: t4,
      onRender: renderName,
      widthPercent: 20
    };
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t) {
    t6 = t("dashboard.sub_accounts.resources");
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== renderResources || $[22] !== t6) {
    t7 = {
      key: "resources",
      header: t6,
      onRender: renderResources,
      widthPercent: 20
    };
    $[21] = renderResources;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t) {
    t8 = t("dashboard.sub_accounts.last_30_days");
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t) {
    t9 = t("dashboard.sub_accounts.statistics");
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <Hint label={t8}>{t9}</Hint>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== renderStatistics || $[32] !== t10) {
    t11 = {
      key: "statistics",
      header: t10,
      onRender: renderStatistics
    };
    $[31] = renderStatistics;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t11 || $[35] !== t5 || $[36] !== t7) {
    t12 = [t5, t7, t11];
    $[34] = t11;
    $[35] = t5;
    $[36] = t7;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const columns = t12;
  let t13;
  if ($[38] !== createSubAccount || $[39] !== mutateUser || $[40] !== revalidateSubAccountsModule) {
    t13 = async subAccount_2 => {
      await fetchWithErrors(async () => {
        const newSubAccount = await createSubAccount(subAccount_2);
        revalidateSubAccountsModule(true);
        await mutateUser(data_0 => {
          if (!data_0) {
            return;
          }
          if (hasSubAccounts(data_0)) {
            return {
              ...data_0,
              subaccounts: [...data_0.subaccounts, newSubAccount]
            };
          } else {
            return {
              ...data_0,
              subaccounts: [newSubAccount]
            };
          }
        }, false);
      }, setErrors);
    };
    $[38] = createSubAccount;
    $[39] = mutateUser;
    $[40] = revalidateSubAccountsModule;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const onCreate = t13;
  let t14;
  if ($[42] !== editSubAccount || $[43] !== mutate || $[44] !== mutateUser || $[45] !== revalidateSubAccountsModule) {
    t14 = async (oldData, updatedData) => {
      await fetchWithErrors(async () => {
        const returnedData = await editSubAccount(oldData.user.id, {
          name: updatedData.displayName
        });
        await mutate(data_1 => data_1 && {
          items: data_1.items.map(item => {
            if (item.id === oldData.id) {
              item = returnedData;
            }
            return item;
          }),
          hasMore: data_1.hasMore
        }, false);
        await mutateUser(data_2 => {
          if (!data_2 || !hasSubAccounts(data_2)) {
            return;
          }
          return {
            ...data_2,
            subaccounts: data_2.subaccounts.map(item_0 => {
              if (item_0.id === oldData.id) {
                return returnedData;
              }
              return item_0;
            })
          };
        }, false);
      }, setErrors);
      revalidateSubAccountsModule();
    };
    $[42] = editSubAccount;
    $[43] = mutate;
    $[44] = mutateUser;
    $[45] = revalidateSubAccountsModule;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const onEdit = t14;
  let t15;
  if ($[47] !== deleteSubAccount || $[48] !== mutateUser || $[49] !== revalidateSubAccountsModule) {
    t15 = async subAccount_3 => {
      await deleteSubAccount(subAccount_3.user.id);
      revalidateSubAccountsModule(true);
      await mutateUser(data_3 => {
        if (!data_3 || !hasSubAccounts(data_3)) {
          return;
        }
        return {
          ...data_3,
          subaccounts: data_3.subaccounts.filter(item_1 => item_1.user.id !== subAccount_3.user.id)
        };
      }, false);
    };
    $[47] = deleteSubAccount;
    $[48] = mutateUser;
    $[49] = revalidateSubAccountsModule;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const onRemove = t15;
  let t16;
  if ($[51] !== router) {
    t16 = subAccount_4 => {
      router.push(generatePretendUserUrl(subAccount_4.user.id));
    };
    $[51] = router;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const onImpersonateSubAccount = t16;
  const t17 = counter?.count;
  let t18;
  if ($[53] !== t17 || $[54] !== tICU) {
    t18 = tICU("dashboard.sub_accounts.title_icu", {
      count: t17
    });
    $[53] = t17;
    $[54] = tICU;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t) {
    t19 = t("dashboard.sub_account.title");
    $[56] = t;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  const t20 = !subAccounts;
  let t21;
  if ($[58] !== t) {
    t21 = t("dashboard.sub_account.toast_create");
    $[58] = t;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = ["name", "domain"];
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== errors || $[62] !== t) {
    t23 = {
      field: "name",
      onRender: (value_0, onChange) => <FormInput required={true} key="sub-account-name-create" label={t("global.name")} value={value_0} onChange={e => onChange(e.target.value)} error={errors.name} />
    };
    $[61] = errors;
    $[62] = t;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== errors || $[65] !== t) {
    t24 = {
      field: "domain",
      onRender: (value_1, onChange_0, state) => <FormInputGroup required={true} key="sub-account-domain-create" label={t("dashboard.sub_accounts.sub_domain")} error={errors.domain} prefix={`${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://`} suffix={isClient() ? `.${replaceWwwForHosts(window.location.host)}` : undefined} onChange={e_0 => onChange_0(e_0.target.value)} onFocus={e_1 => {
        if (!e_1.target.value) {
          onChange_0(generatePathFromName(state.name));
        }
      }} value={value_1} />
    };
    $[64] = errors;
    $[65] = t;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== t23 || $[68] !== t24) {
    t25 = [t23, t24];
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== onCreate || $[71] !== t19 || $[72] !== t20 || $[73] !== t21 || $[74] !== t25) {
    t26 = {
      defaultValues: subAccountDefaultCreateValues,
      onCreate,
      caption: t19,
      disabled: t20,
      toastCaption: t21,
      requiredFields: t22,
      creatable: t25
    };
    $[70] = onCreate;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t) {
    t27 = t("dashboard.sub_account.title");
    $[76] = t;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== onRemove) {
    t28 = {
      onChange: onRemove
    };
    $[78] = onRemove;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== errors || $[81] !== t) {
    t29 = [{
      field: "displayName",
      onRender: (value_2, onChange_1) => <FormInput required={true} key="sub-account-display-name-edit" label={t("global.name")} value={value_2} onChange={e_2 => onChange_1(e_2.target.value)} error={errors.name} />
    }];
    $[80] = errors;
    $[81] = t;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== onEdit || $[84] !== t29) {
    t30 = {
      onEdit,
      onModalClose,
      editable: t29
    };
    $[83] = onEdit;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== t) {
    t31 = t("dashboard.sub_account.impersonate");
    $[86] = t;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] !== onImpersonateSubAccount) {
    t32 = instance => onImpersonateSubAccount(instance);
    $[88] = onImpersonateSubAccount;
    $[89] = t32;
  } else {
    t32 = $[89];
  }
  let t33;
  if ($[90] !== t31 || $[91] !== t32) {
    t33 = [{
      caption: t31,
      name: "impersonateToSubAccount",
      onActionClick: t32
    }];
    $[90] = t31;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== t28 || $[94] !== t30 || $[95] !== t33) {
    t34 = {
      remove: t28,
      settings: t30,
      custom: t33
    };
    $[93] = t28;
    $[94] = t30;
    $[95] = t33;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] !== limit || $[98] !== setLimit) {
    t35 = {
      limit,
      setLimit
    };
    $[97] = limit;
    $[98] = setLimit;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] !== pagination || $[101] !== setPagination) {
    t36 = {
      pagination,
      setPagination
    };
    $[100] = pagination;
    $[101] = setPagination;
    $[102] = t36;
  } else {
    t36 = $[102];
  }
  let t37;
  if ($[103] !== renderResources || $[104] !== renderStatistics) {
    t37 = (subAccount_5, actions) => <div className="flex w-full min-w-0 flex-col gap-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0 flex-1">{renderName(subAccount_5)}</div><div className="flex h-10 shrink-0 items-center">{actions}</div></div>{renderResources(subAccount_5)}{renderStatistics(subAccount_5)}</div>;
    $[103] = renderResources;
    $[104] = renderStatistics;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  let t38;
  if ($[106] !== columns || $[107] !== subAccounts || $[108] !== t27 || $[109] !== t34 || $[110] !== t35 || $[111] !== t36 || $[112] !== t37) {
    t38 = <Table columns={columns} caption={t27} data={subAccounts} actions={t34} limiter={t35} paginator={t36} renderMobileRow={t37} />;
    $[106] = columns;
    $[107] = subAccounts;
    $[108] = t27;
    $[109] = t34;
    $[110] = t35;
    $[111] = t36;
    $[112] = t37;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  let t39;
  if ($[114] !== counter || $[115] !== t18 || $[116] !== t26 || $[117] !== t38) {
    t39 = <ListingLayout counter={counter} title={t18} createData={t26}>{t38}</ListingLayout>;
    $[114] = counter;
    $[115] = t18;
    $[116] = t26;
    $[117] = t38;
    $[118] = t39;
  } else {
    t39 = $[118];
  }
  return t39;
}
function _temp(subAccount) {
  return <div className="flex items-center gap-2">{subAccount.profileImage && <span className="shrink-0"><Avatar size="lg" src={subAccount.profileImage.path} displayName={subAccount.displayName} alt={subAccount.displayName} /></span>}<div className="flex min-w-0 flex-col" title={subAccount.displayName}><span className="truncate">{subAccount.displayName}</span></div></div>;
}
export const getServerSideProps = getCombinedServerSideProps(getBaseServerSideProps(SubAccountsLoco, 'header.top_menu.sub_accounts'));
export default SubAccounts;